import axios from "axios";
import { store } from "@/store";
import { logout } from "@/store/slices/authSlice";

const UTILS_API_BASE_URL = import.meta.env.VITE_UTILS_API_URL;

export interface OpportunitySource {
  id: string;
  slug: string;
  name: string;
  is_default: boolean;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface CreateOpportunitySourceData {
  slug: string;
  name: string;
  is_default?: boolean;
  is_active?: boolean;
}

export interface UpdateOpportunitySourceData extends Partial<CreateOpportunitySourceData> {}

export interface SourcesResponse {
  success: boolean;
  data: OpportunitySource[];
  count: number;
}

export interface SourceResponse {
  success: boolean;
  data: OpportunitySource;
}

export interface SourcesQueryParams {
  slug: string;
  is_active?: boolean;
}

export const opportunitySourcesAPI = {
  /**
   * Obtiene lista de fuentes de oportunidad
   */
  getSources: async (params: SourcesQueryParams): Promise<SourcesResponse> => {
    try {
      const queryParams = new URLSearchParams();
      queryParams.append("slug", params.slug);

      if (params.is_active !== undefined) {
        const isActiveValue = params.is_active ? 1 : 0;
        queryParams.append("is_active", isActiveValue.toString());
      }

      const response = await utilsApi.get(
        `/opportunity-sources?${queryParams.toString()}`,
      );

      if (response.data.success) {
        return {
          success: true,
          data: response.data.data,
          count: response.data.count,
        };
      } else {
        throw new Error(
          response.data?.message || "Error al obtener fuentes de oportunidad",
        );
      }
    } catch (error: any) {
      if (error.response) {
        throw new Error(error.response.data?.message || "Error en el servidor");
      } else if (error.request) {
        throw new Error("No se pudo conectar con el servidor");
      } else {
        throw new Error("Error al configurar la solicitud");
      }
    }
  },

  /**
   * Obtiene una fuente de oportunidad por ID
   */
  getSourceById: async (id: string, slug: string): Promise<SourceResponse> => {
    try {
      const response = await utilsApi.get(
        `/opportunity-sources/${id}?slug=${slug}`,
      );

      if (response.data.success) {
        return {
          success: true,
          data: response.data.data,
        };
      } else {
        throw new Error(
          response.data?.message || "Error al obtener la fuente de oportunidad",
        );
      }
    } catch (error: any) {
      if (error.response) {
        throw new Error(error.response.data?.message || "Error en el servidor");
      } else if (error.request) {
        throw new Error("No se pudo conectar con el servidor");
      } else {
        throw new Error("Error al configurar la solicitud");
      }
    }
  },

  /**
   * Crea una nueva fuente de oportunidad
   */
  createSource: async (
    sourceData: CreateOpportunitySourceData,
  ): Promise<SourceResponse> => {
    try {
      const response = await utilsApi.post(
        `/opportunity-sources`,
        sourceData,
      );

      if (response.data.success) {
        return {
          success: true,
          data: response.data.data,
        };
      } else {
        throw new Error(
          response.data?.message || "Error al crear la fuente de oportunidad",
        );
      }
    } catch (error: any) {
      if (error.response) {
        throw new Error(error.response.data?.message || "Error en el servidor");
      } else if (error.request) {
        throw new Error("No se pudo conectar con el servidor");
      } else {
        throw new Error("Error al configurar la solicitud");
      }
    }
  },

  /**
   * Actualiza una fuente de oportunidad existente
   */
  updateSource: async (
    id: string,
    sourceData: UpdateOpportunitySourceData,
  ): Promise<SourceResponse> => {
    try {
      const payload = { ...sourceData };
      if (!payload.slug && sourceData.slug) {
        payload.slug = sourceData.slug;
      }

      const response = await utilsApi.put(
        `/opportunity-sources/${id}`,
        payload,
      );

      if (response.data.success) {
        return {
          success: true,
          data: response.data.data,
        };
      } else {
        throw new Error(
          response.data?.message ||
            "Error al actualizar la fuente de oportunidad",
        );
      }
    } catch (error: any) {
      console.error("❌ Error en updateSource:", {
        message: error.message,
        response: error.response?.data,
        config: error.config,
      });
      if (error.response) {
        throw new Error(error.response.data?.message || "Error en el servidor");
      } else if (error.request) {
        throw new Error("No se pudo conectar con el servidor");
      } else {
        throw new Error("Error al configurar la solicitud");
      }
    }
  },

  /**
   * Elimina una fuente de oportunidad
   */
  deleteSource: async (
    id: string,
    slug: string,
  ): Promise<{ success: boolean; message: string }> => {
    try {
      const response = await utilsApi.delete(
        `/opportunity-sources/${id}?slug=${slug}`,
      );

      if (response.data.success) {
        return {
          success: true,
          message:
            response.data.message ||
            "Fuente de oportunidad eliminada exitosamente",
        };
      } else {
        throw new Error(
          response.data?.message ||
            "Error al eliminar la fuente de oportunidad",
        );
      }
    } catch (error: any) {
      if (error.response) {
        throw new Error(error.response.data?.message || "Error en el servidor");
      } else if (error.request) {
        throw new Error("No se pudo conectar con el servidor");
      } else {
        throw new Error("Error al configurar la solicitud");
      }
    }
  },

  /**
   * Obtiene la fuente de oportunidad por defecto
   */
  getDefaultSource: async (slug: string): Promise<SourceResponse> => {
    try {
      const response = await utilsApi.get(
        `/opportunity-sources?slug=${slug}&is_default=true&is_active=1`,
      );

      if (response.data.success) {
        const defaultSource = response.data.data.find(
          (source: OpportunitySource) => source.is_default,
        );

        if (defaultSource) {
          return {
            success: true,
            data: defaultSource,
          };
        } else {
          throw new Error(
            "No se encontró una fuente de oportunidad por defecto",
          );
        }
      } else {
        throw new Error(
          response.data?.message || "Error al obtener la fuente por defecto",
        );
      }
    } catch (error: any) {
      if (error.response) {
        throw new Error(error.response.data?.message || "Error en el servidor");
      } else if (error.request) {
        throw new Error("No se pudo conectar con el servidor");
      } else {
        throw new Error("Error al configurar la solicitud");
      }
    }
  },

  /**
   * Marca una fuente como por defecto (deselecciona las otras)
   */
  setDefaultSource: async (
    id: string,
    slug: string,
  ): Promise<SourceResponse> => {
    try {
      const allSourcesResponse = await utilsApi.get(
        `/opportunity-sources?slug=${slug}`,
      );

      if (!allSourcesResponse.data.success) {
        throw new Error("No se pudieron obtener las fuentes");
      }

      const allSources = allSourcesResponse.data.data;

      const defaultSources = allSources.filter(
        (source: OpportunitySource) => source.is_default,
      );

      const removeDefaultPromises = defaultSources
        .map((source: OpportunitySource) => {
          if (source.id !== id) {
            return utilsApi.put(`/opportunity-sources/${source.id}`, {
              is_default: false,
              slug,
            });
          }
          return Promise.resolve();
        })
        .filter((promise) => promise !== undefined);

      if (removeDefaultPromises.length > 0) {
        await Promise.all(removeDefaultPromises);
      }

      const response = await utilsApi.put(`/opportunity-sources/${id}`, {
        is_default: true,
        slug,
      });

      if (response.data.success) {
        return {
          success: true,
          data: response.data.data,
        };
      } else {
        throw new Error(
          response.data?.message || "Error al establecer la fuente por defecto",
        );
      }
    } catch (error: any) {
      console.error("❌ Error en setDefaultSource:", {
        message: error.message,
        response: error.response?.data,
        stack: error.stack,
      });
      throw new Error(
        error.message || "Error al establecer la fuente por defecto",
      );
    }
  },
};

export const utilsApi = axios.create({
  baseURL: UTILS_API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

utilsApi.interceptors.request.use(
  (config) => {
    const token =
      localStorage.getItem("token") || sessionStorage.getItem("token");

    const publicEndpoints = ["/login"];
    const isPublicEndpoint = publicEndpoints.some((endpoint) =>
      config.url?.endsWith(endpoint),
    );

    if (token && !isPublicEndpoint) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error) => Promise.reject(error),
);

utilsApi.interceptors.response.use(
  (response) => {
    if (
      response.data &&
      (response.data.statusCode === 200 || response.data.statusCode === 201) &&
      response.data.body
    ) {
      response.data = response.data.body;
    }
    return response;
  },
  (error) => {
    if (error.response?.status === 401) {
      store.dispatch(logout());
      window.location.hash = "#/login";
    }

    if (error.response?.data?.body?.message) {
      error.message = error.response.data.body.message;
    }

    return Promise.reject(error);
  },
);
